<template>
	<div class="results-toolbar results-toolbar--transactions">
		<div class="results-toolbar--transactions__desktop" ref="desktopRef">
			<div class="results-toolbar--transactions__date">
				<DatePicker v-model="parseDate" @change="emitValues" />
			</div>
			<div class="results-toolbar--transactions__filters">
				<Button
					v-if="showDefaultFilters"
					class="results-toolbar--transactions__reset-filters"
					className="btn--link"
					labelText="Restore Filters"
					:btnClick="resetFilters"
				/>

				<DropdownV2
					class="results-toolbar--transactions__level"
					v-model="filters.level"
					:options="levelOptions"
					placeholder="Select Level"
					@change="emitValues"
					ref="dropdownV2Ref"
				/>
				<DropdownV2
					v-model="filters.dealershipGroupGuid"
					:options="customerOptions"
					placeholder="Select Client"
					@change="emitValuesFromCustomerDropdown"
				/>
				<DropdownV2
					:class="[!companyItems.length && 'is-disabled']"
					v-model="filters.companyId"
					:options="companyItems"
					placeholder="Select Company"
					@change="emitValues"
					ref="selectCompanyDropdownRef"
				/>
				<DropdownV2
					v-model="filters.bodType"
					:options="bodOptions"
					placeholder="Select Bod Type"
					@change="emitValues"
				/>
			</div>
		</div>
		<div class="results-toolbar--transactions__mobile" ref="mobileRef">
			<DatePicker v-model="parseDate" @change="emitValues" />
			<Button
				className="btn--primary"
				iconClassName="icon-filter"
				:iconBeforeLabel="true"
				:btnClick="() => (displayFiltersModal = true)"
				ref="displayFiltersModalBtnRef"
			/>
		</div>
		<Modal
			v-if="displayFiltersModal"
			:display="true"
			title="Apply Filters"
			@show="displayFiltersModal = false"
			ref="modalRef"
		>
			<template #modal-content>
				<DropdownV2
					v-model="filtersModal.level"
					:options="levelOptions"
					placeholder="Select Level"
				/>
				<DropdownV2
					v-model="filtersModal.dealershipGroupGuid"
					:options="customerOptions"
					placeholder="Select Client"
				/>
				<DropdownV2
					:class="[!companyItems.length && 'is-disabled']"
					v-model="filtersModal.companyId"
					:options="companyItems"
					placeholder="Select Company"
				/>
				<DropdownV2
					v-model="filtersModal.bodyType"
					:options="bodOptions"
					placeholder="Select Bod Type"
				/>
			</template>
			<template #modal-footer>
				<ButtonGroup>
					<template #container>
						<Button
							labelText="Apply Filters"
							className="btn--primary"
							:btnClick="applyFilterUsingModal"
							:disabled="!filtersModalHaveChanges"
							ref="modalApplyBtnRef"
						/>
						<Button
							labelText="Close"
							className="btn--link"
							:btnClick="closeModalFilters"
						/>
					</template>
				</ButtonGroup>
			</template>
		</Modal>
	</div>
</template>

<script>
import DatePicker from '@/components/forms/date-picker/date-picker';
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import Modal from '@/components/shells/modal/modals';

// libraries
import { formatDate } from '@/utils/datetime';
import { parse } from 'date-fns';
import { deepEqualNoFn } from '@/utils/object';
import _ from 'lodash';

// vuex
import { mapGetters, mapActions } from 'vuex';

export default {
	name: 'IntegrationTransactionsFilters',
	components: {
		DatePicker,

		ButtonGroup,
		Button,
		DropdownV2,
		Modal
	},
	props: {
		value: {
			type: Object,
			default: () => {}
		},
		defaultFilters: Object,
		customerOptions: Array,
		bodOptions: Array
	},
	data() {
		return {
			displayFiltersModal: false,
			filters: {},
			filtersModal: {},
			levelOptions: [
				{ text: 'Error', value: 'ERROR' },
				{ text: 'Info', value: 'INFO' }
			]
		};
	},
	created() {
		this.updateLocalValues(this.value);
	},
	computed: {
		...mapGetters('options', ['companyItems']),
		filtersModalHaveChanges() {
			return !deepEqualNoFn(this.filters, this.filtersModal, true);
		},
		showDefaultFilters() {
			return !deepEqualNoFn(this.filters, this.defaultFilters);
		},
		parseDate: {
			get() {
				if (this.filters.minDatetime) {
					return {
						startDate: parse(
							this.filters.minDatetime,
							'MM-dd-yyyy',
							new Date()
						),
						endDate: parse(
							this.filters.maxDatetime,
							'MM-dd-yyyy',
							new Date()
						)
					};
				} else {
					return {
						startDate: parse(
							this.defaultFilters.minDatetime,
							'MM-dd-yyyy',
							new Date()
						),
						endDate: parse(
							this.defaultFilters.maxDatetime,
							'MM-dd-yyyy',
							new Date()
						)
					};
				}
			},
			set(value) {
				if (value.startDate) {
					this.filters.minDatetime = formatDate(value.startDate);
					this.filters.maxDatetime = formatDate(value.endDate);
				} else {
					this.filters.minDatetime = this.defaultFilters.minDatetime;
					this.filters.maxDatetime = this.defaultFilters.maxDatetime;
				}
			}
		}
	},
	methods: {
		...mapActions('options', ['getCompanies']),

		updateLocalValues(obj) {
			this.filters = _.cloneDeep(obj);
			this.filtersModal = _.cloneDeep(obj);
		},
		applyFilterUsingModal() {
			this.filters = _.cloneDeep(this.filtersModal);
			this.displayFiltersModal = false;
			this.$emit('input', { ...this.filters });
		},
		resetFilters() {
			this.updateLocalValues(this.defaultFilters);

			this.$emit('input', { ...this.filters });
		},
		closeModalFilters() {
			this.filtersModal = _.cloneDeep(this.filters);
			this.displayFiltersModal = false;
		},
		emitValues() {
			this.filtersModal = _.cloneDeep(this.filters);
			this.$emit('input', { ...this.filters });
		},
		emitValuesFromCustomerDropdown() {
			if (!this.filters.dealershipGroupGuid && this.filters.companyId) {
				this.filters.companyId = 0;
			}
			this.emitValues();
		},
		async getCustomersByDealershipGroupGuid(val) {
			// get companies
			if (val) {
				try {
					await this.getCompanies(this.filters.dealershipGroupGuid);
				} catch (err) {
					console.log(err);
				}
			} else {
				this.$store.commit('options/clearCompanies');
			}
		}
	},
	watch: {
		'filters.dealershipGroupGuid': {
			immediate: true,
			handler(val) {
				this.getCustomersByDealershipGroupGuid(val);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.results-toolbar--transactions {
	&__reset-filters {
		margin-left: auto;
	}

	&__mobile {
		margin-left: $gutter-lg;

		@include mq($from: md) {
			margin-left: 0;
		}

		@include mq($from: md-l) {
			display: none;
		}
	}

	&__desktop {
		width: 100%;
		display: none;
		z-index: 100;

		@include mq($from: md-l) {
			display: flex;
			gap: $gutter-lg;
		}
	}

	&__filters {
		display: flex;
		justify-content: flex-end;
		gap: $gutter-lg;
		flex-grow: 2;
	}

	&__counter {
		text-align: right;
		margin: $gutter-md 0;
	}
}
</style>
